import { useState } from 'react';
import { BarChart3, Download, TrendingUp, Users, Mail, Handshake } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchJson } from '../lib/api';
import { Badge } from '../components/shared/Badge';
import { cn } from '../lib/cn';

interface ReportsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

interface PipelineReport {
  stages: { name: string; count: number; totalPremium: number; color?: string }[];
  totalDeals: number;
  totalPremium: number;
  wonCount: number;
  lostCount: number;
  winRate: number;
}

interface ActivityReport {
  thisWeek: number;
  thisMonth: number;
  byChannel: { channel: string; count: number }[];
}

interface ContactGrowth {
  periods: { label: string; count: number }[];
  totalNew: number;
}

interface TopContact {
  id: string;
  name: string;
  interactionCount: number;
  lastActivity: string;
}

interface ReportsData {
  pipeline: PipelineReport;
  activity: ActivityReport;
  contactGrowth: ContactGrowth;
  topContacts: TopContact[];
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

export function ReportsView({ onNavigate }: ReportsViewProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports'],
    queryFn: () => fetchJson<ReportsData>('/api/reports'),
    staleTime: 5 * 60_000,
  });

  function handleExportCsv() {
    // Trigger CSV download
    window.open('/api/reports/export?format=csv', '_blank');
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Reports</h1>
          <p className="mt-0.5 text-sm text-slate-500">Pipeline and activity analytics</p>
        </div>
        <button className="btn-secondary" onClick={handleExportCsv}>
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      <div className="p-6 space-y-6">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-64 animate-pulse rounded-lg bg-surface-200" />
            ))}
          </div>
        ) : error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load reports. Please try again.
          </div>
        ) : data ? (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="stat-card">
                <div className="flex items-center gap-2">
                  <div className="rounded-lg bg-green-50 p-2 text-green-600">
                    <Handshake className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-2xl font-bold text-slate-900">{data.pipeline.totalDeals}</div>
                  <div className="text-sm text-slate-500">Total Deals</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="flex items-center gap-2">
                  <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-2xl font-bold text-slate-900">{formatCurrency(data.pipeline.totalPremium)}</div>
                  <div className="text-sm text-slate-500">Total Premium</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="flex items-center gap-2">
                  <div className="rounded-lg bg-purple-50 p-2 text-purple-600">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-2xl font-bold text-slate-900">
                    {Math.round(data.pipeline.winRate * 100)}%
                  </div>
                  <div className="text-sm text-slate-500">Win Rate</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="flex items-center gap-2">
                  <div className="rounded-lg bg-orange-50 p-2 text-orange-600">
                    <Mail className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-2xl font-bold text-slate-900">{data.activity.thisWeek}</div>
                  <div className="text-sm text-slate-500">Interactions This Week</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Pipeline by Stage */}
              <div className="card p-4">
                <h3 className="mb-4 text-sm font-semibold text-slate-700">Deals by Stage</h3>
                <div className="space-y-3">
                  {data.pipeline.stages.map((stage) => {
                    const pct = data.pipeline.totalDeals > 0
                      ? (stage.count / data.pipeline.totalDeals) * 100
                      : 0;
                    return (
                      <div key={stage.name}>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            {stage.color && (
                              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                            )}
                            <span className="text-slate-700">{stage.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-slate-500">{stage.count} deals</span>
                            <span className="font-medium text-slate-700">{formatCurrency(stage.totalPremium)}</span>
                          </div>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-surface-100">
                          <div
                            className="h-full rounded-full bg-brand-500 transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: stage.color ?? undefined,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center gap-4 border-t border-surface-100 pt-3 text-sm">
                  <span className="text-slate-500">Won: <strong className="text-green-600">{data.pipeline.wonCount}</strong></span>
                  <span className="text-slate-500">Lost: <strong className="text-red-600">{data.pipeline.lostCount}</strong></span>
                </div>
              </div>

              {/* Activity by Channel */}
              <div className="card p-4">
                <h3 className="mb-4 text-sm font-semibold text-slate-700">Activity by Channel</h3>
                <div className="space-y-3">
                  {data.activity.byChannel.map((ch) => {
                    const maxCount = Math.max(...data.activity.byChannel.map((c) => c.count), 1);
                    const pct = (ch.count / maxCount) * 100;
                    return (
                      <div key={ch.channel}>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="capitalize text-slate-700">{ch.channel}</span>
                          <span className="text-slate-500">{ch.count}</span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-surface-100">
                          <div
                            className="h-full rounded-full bg-blue-500 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 border-t border-surface-100 pt-3 text-sm text-slate-500">
                  This month: <strong className="text-slate-700">{data.activity.thisMonth}</strong> interactions
                </div>
              </div>

              {/* Contact Growth */}
              <div className="card p-4">
                <h3 className="mb-4 text-sm font-semibold text-slate-700">Contact Growth</h3>
                <div className="space-y-2">
                  {data.contactGrowth.periods.map((period) => {
                    const maxCount = Math.max(...data.contactGrowth.periods.map((p) => p.count), 1);
                    const pct = (period.count / maxCount) * 100;
                    return (
                      <div key={period.label} className="flex items-center gap-3">
                        <span className="w-20 text-xs text-slate-500">{period.label}</span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-100">
                          <div
                            className="h-full rounded-full bg-purple-500 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-8 text-right text-xs font-medium text-slate-600">{period.count}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 border-t border-surface-100 pt-3 text-sm text-slate-500">
                  Total new: <strong className="text-slate-700">{data.contactGrowth.totalNew}</strong>
                </div>
              </div>

              {/* Top Contacts */}
              <div className="card p-4">
                <h3 className="mb-4 text-sm font-semibold text-slate-700">Top Contacts by Interactions</h3>
                <div className="divide-y divide-surface-100">
                  {data.topContacts.map((contact, i) => (
                    <button
                      key={contact.id}
                      onClick={() => onNavigate('contact-detail', { contactId: contact.id })}
                      className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-surface-50"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-100 text-2xs font-semibold text-slate-500">
                        {i + 1}
                      </span>
                      <span className="flex-1 text-sm font-medium text-slate-700">{contact.name}</span>
                      <Badge color="blue" size="sm">{contact.interactionCount}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
